import { useEffect } from 'react'
import { AlertCircle, CheckCircle, X } from 'lucide-react'

interface ToastProps {
  type: 'error' | 'success'
  title?: string
  message: string
  isVisible: boolean
  onClose: () => void
  autoClose?: boolean
  autoCloseDelay?: number
}

export function Toast({ 
  type, 
  title, 
  message, 
  isVisible, 
  onClose, 
  autoClose = true, 
  autoCloseDelay = 5000 
}: ToastProps) {
  useEffect(() => {
    if (isVisible && autoClose) {
      const timer = setTimeout(() => {
        onClose()
      }, autoCloseDelay)
      
      return () => clearTimeout(timer)
    }
  }, [isVisible, autoClose, autoCloseDelay, onClose])

  if (!isVisible) return null

  const isError = type === 'error'

  return (
    <div className="fixed top-16 left-4 right-4 z-[9999] animate-in slide-in-from-top-2 duration-300">
      <div className={`
        flex w-full max-w-[300px] mx-auto items-start gap-2 p-3 relative rounded-[20px] border border-solid
        ${isError 
          ? 'bg-[#fff0f9] border-[#fed2e8]' 
          : 'bg-white border-[#E5E7EC]'
        }
      `}>
        {/* Success toast 左侧绿色条 */}
        {!isError && (
          <div className="absolute w-[41px] h-full top-0 left-0 bg-[#82dcb9] rounded-[20px_0px_0px_20px]" />
        )}

        {/* 图标 */}
        <div className="relative flex-shrink-0">
          {isError ? (
            <AlertCircle className="w-6 h-6 text-[#ED4B9E]" />
          ) : (
            <CheckCircle className="w-6 h-6 text-white" />
          )}
        </div>

        {/* 内容区域 */}
        <div className="flex-1 grow flex flex-col items-start relative">
          <div className="pt-0.5 pb-0 px-0 self-stretch w-full flex-[0_0_auto] flex flex-col items-start relative">
            {/* 标题 - 仅在有标题或者是 success 类型时显示 */}
            {(title || !isError) && (
              <div className="relative w-full mt-[-1.00px] font-kanit font-bold text-[#280d5f] text-sm tracking-[0] leading-[21px] break-words">
                {title || (isError ? 'Error' : 'Success')}
              </div>
            )}

            {/* 消息内容 */}
            <div className={`
              relative w-full font-kanit font-${isError ? 'normal' : 'medium'} text-xs tracking-[0] leading-[18px] break-words
              ${isError ? 'text-[#280d5f]' : 'text-[#82dcb9]'}
              ${title || !isError ? 'mt-1' : 'mt-[-1.00px]'}
            `}>
              {message}
            </div>
          </div>
        </div>

        {/* 关闭按钮 */}
        <button 
          onClick={onClose}
          className="relative flex-shrink-0 w-5 h-5 flex items-center justify-center hover:bg-black/10 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-[#280d5f]" />
        </button>
      </div>
    </div>
  )
} 